/**
 * Post-login redirect targets.
 *
 * The marketing site sends visitors here to sign in with
 * /signin?redirect=https://www.importjdmmagic.com/dealer-stock/... and expects
 * to get them back afterwards. Anything outside our own domain is refused, so
 * the parameter cannot be used to bounce a freshly signed-in user to a lookalike.
 *
 * Returns { url, external } or null. `external` is true when the target lives on
 * another of our hosts and needs a full navigation rather than a router push.
 */
const OWN_DOMAIN = 'importjdmmagic.com';

const isOwnHost = (hostname) => {
    const host = String(hostname || '').toLowerCase();
    if (host === OWN_DOMAIN || host.endsWith(`.${OWN_DOMAIN}`)) return true;
    // Local development, where each app runs on its own port of localhost.
    return host === 'localhost' || host === '127.0.0.1';
};

export const resolveSafeRedirect = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const value = raw.trim();
    if (!value) return null;

    // Same-app path. A protocol-relative "//host" is not a path.
    if (value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')) {
        return { url: value, external: false };
    }

    let url;
    try {
        url = new URL(value);
    } catch (err) {
        return null;
    }

    const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocal)) return null;
    if (!isOwnHost(url.hostname)) return null;

    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
        return { url: `${url.pathname}${url.search}${url.hash}`, external: false };
    }
    return { url: url.href, external: true };
};

/** Navigate to a resolved redirect, or to the fallback path when there is none. */
export const followRedirect = (target, navigate, fallback = '/') => {
    if (target && target.external) {
        window.location.assign(target.url);
        return;
    }
    navigate(target ? target.url : fallback, { replace: true });
};
